import { useMutation } from '@tanstack/react-query'
import { SERVER_URL } from '@/env-vars'
import { Store } from '@/store'
import { useAuthFromStorage } from '@/hooks/useAuthStorage'
import { useSyncChat } from '@/mutations/useSyncChat'
import { toast } from 'sonner'
import * as Sentry from '@sentry/react'
import { errors } from '@/i18n/errors'
import { analytics } from '@/lib/analytics'

export function useGptStream() {
	const { authToken } = useAuthFromStorage()
	const syncChat = useSyncChat()

	return useMutation({
		mutationKey: ['gpt-stream'],
		retry: false,
		mutationFn: async ({ chatId, abortController }: { chatId: string; abortController: AbortController }) => {
			const messages = Store.useBoundStore.getState().chats[chatId].messages
			// Strip the trailing empty assistant placeholder that was appended to the
			// store before streaming started. Gemini requires the last turn to be a
			// user turn — sending an empty model message causes a silent API failure.
			const messagesWithoutPlaceholder = messages.at(-1)?.role === 'assistant' &&
				messages.at(-1)?.content === ''
				? messages.slice(0, -1)
				: messages
			const lastMessages = messagesWithoutPlaceholder.slice(-6)
			const timeout = 60000 // Timeout in milliseconds — Gemini 2.5 Pro can think for 30–60s before first token
			let receivedToken = false

			const indexLastMessage = messages.length - 1

			const response = await fetch(`${SERVER_URL}/chat/run`, {
				method: 'POST',
				headers: {
					Authorization: `Bearer ${authToken}`,
					'Content-Type': 'application/json',
					Connection: 'keep-alive',
				},
				body: JSON.stringify({
					chatId: chatId,
					messages: lastMessages,
				}),
				signal: abortController.signal,
			})

			// check for rate limit error
			if (response.status === 429) {
				Sentry.captureEvent({
					message: 'GPT API Rate Limit Hit',
				})
				toast('Please Try Again Tommorow', {
					description: 'Your Daily Rate Limit has Been Reached',
					dismissible: true,
					duration: 5 * 1000,
					action: {
						label: 'OK',
						onClick: () => {},
					},
				})
				return { success: false }
			}

			if (response.status > 300) {
				throw Error(response.status + ':' + response.statusText)
			}

			const timeoutId = setTimeout(() => {
				if (!receivedToken) {
					abortController.abort()
				}
			}, timeout)

			const reader = response.body!.getReader()
			const decoder = new TextDecoder()
			let buffer = ''

			try {
				while (true) {
					const { done, value } = await reader.read()
					if (done) break

					buffer += decoder.decode(value, { stream: true })
					const lines = buffer.split('\n')
					buffer = lines.pop() ?? ''

					for (const line of lines) {
						if (!line.startsWith('data: ')) continue
						const data = line.slice(6).trim()
						if (!data || data === '[DONE]') continue
						try {
							const parsed = JSON.parse(data)
							if (parsed?.token) {
								receivedToken = true
								Store.updateChatMessage({ chatId, content: parsed.token, messageIdx: indexLastMessage })
							}
						} catch {
							// malformed chunk, skip
						}
					}
				}
			} finally {
				reader.releaseLock()
			}

			clearTimeout(timeoutId)
			return { success: true }
		},
		onSuccess: ({ success }) => {
			if (success) {
				analytics.chatResponseReceived()
				syncChat.mutate()
			}
		},
		onError: (error, { chatId }) => {
			const chat = Store.useBoundStore.getState().chats[chatId]
			toast(errors.SERVER_ERROR, {
				dismissible: true,
				action: {
					label: 'Ok',
					onClick: () => {},
				},
				duration: 5 * 1000,
			})
			Sentry.captureException(error, {
				tags: {
					type: 'api-call',
					id: 'gpt-stream',
					numMessages: chat.messages.length,
				},
			})
		},
	})
}
